import { useQuery, useQueryClient } from '@tanstack/react-query'
import type cytoscape from 'cytoscape'
import { AlertTriangle, Clock, FolderOpen, Loader2, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GraphQueryBuilder } from '@/components/graph-query-builder/GraphQueryBuilder'
import { SidebarOpenButton } from '@/components/investigations/SidebarOpenButton'
import type { CytoscapeGraphRef } from '@/components/neo4j/cytoscape/CytoscapeGraph'
import CytoscapeGraph from '@/components/neo4j/cytoscape/CytoscapeGraph'
import type {
  GraphEdge,
  GraphNode,
  SelectedEdge,
  SelectedNode,
} from '@/components/neo4j/cytoscape/types'
import { ExploreFilterPanel } from '@/components/neo4j/explore-filter-panel'
import { BulkDeleteDialog } from '@/components/neo4j/shared/BulkDeleteDialog'
import { CreateGroupDialog } from '@/components/neo4j/shared/CreateGroupDialog'
import { EntityHoverBody } from '@/components/neo4j/shared/EntityHoverPopup'
import { mergeNeighborsIntoGraph } from '@/components/neo4j/shared/entity-details/mergeNeighborsIntoGraph'
import { summarizeEdges } from '@/components/neo4j/shared/entity-details/summarizeEdges'
import type { NeighborEdge, NeighborNode } from '@/components/neo4j/shared/entity-details/types'
import { MultiSelectActionBar } from '@/components/neo4j/shared/MultiSelectActionBar'
import { entityDetailsQueryKey } from '@/components/neo4j/shared/useEntityDetails'
import { entityNeighborsQueryKey } from '@/components/neo4j/shared/useEntityNeighbors'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useGraphExclusion } from '@/hooks/useGraphExclusion'
import { filterFaceEvidence } from './face-visibility'
import { KnowledgeGraphDetailSidebar } from './KnowledgeGraphDetailSidebar'

function BannerDismissible({ nodeCount, totalNodes }: { nodeCount: number; totalNodes: number }) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null
  return (
    <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-xs text-amber-600">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1">
        Showing {nodeCount.toLocaleString()} of {totalNodes.toLocaleString()} entities. Right-click
        a node → "Get More Data" to expand.
      </span>
      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 hover:text-amber-800 transition-colors"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  )
}

// Overlay that explains the timeline ghost state to first-time viewers.
// Stays visible while any node/edge is dimmed due to playhead, auto-hides
// once the cursor reaches the end of the range or everything is "live".
// Session-persistent dismissal via sessionStorage.
function TemporalDimBanner({
  dimCount,
  playheadTime,
  filterEnd,
  caseId,
}: {
  dimCount: number
  playheadTime: string | null
  filterEnd: string | null
  caseId: string
}) {
  const storageKey = `case:${caseId}:timelineBannerDismissed`
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.sessionStorage.getItem(storageKey) === '1'
    } catch {
      return false
    }
  })

  if (dismissed) return null
  if (dimCount === 0) return null
  if (!playheadTime || !filterEnd) return null
  if (new Date(playheadTime).getTime() >= new Date(filterEnd).getTime()) return null

  const handleDismiss = () => {
    try {
      window.sessionStorage.setItem(storageKey, '1')
    } catch {
      // ignore — dismissal still works for current mount
    }
    setDismissed(true)
  }

  return (
    <div
      className="pointer-events-auto absolute top-3 left-3 z-10 max-w-sm animate-in slide-in-from-left-2 fade-in duration-300 max-md:right-16"
      role="note"
      aria-label="Timeline ghost state"
    >
      <div className="relative flex items-start gap-3 rounded-md border border-cyan-400/25 bg-slate-950/85 pl-3.5 pr-2.5 py-2.5 shadow-lg backdrop-blur-md">
        <span
          className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-md bg-cyan-400"
          aria-hidden="true"
        />
        <Clock className="h-4 w-4 shrink-0 text-cyan-300 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0 text-[11px] leading-relaxed text-cyan-100/80">
          <div className="font-semibold text-cyan-100 tracking-wide uppercase text-[10px] mb-0.5">
            Temporal ghost view
          </div>
          <p>
            Dimmed entities and relationships haven&#39;t happened yet at the cursor position.
            Static ones stay fully lit. Press{' '}
            <kbd className="px-1 py-0.5 mx-0.5 bg-cyan-400/15 text-cyan-200 rounded text-[10px] font-mono align-middle">
              ▶
            </kbd>{' '}
            and watch them light up as time flows.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 rounded p-0.5 text-cyan-100/50 transition-colors hover:bg-cyan-400/10 hover:text-cyan-100"
          aria-label="Dismiss banner"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

import { CreateRelationshipDialog } from '@/components/neo4j/CreateRelationshipDialog'
import { ConnectionTargetBanner } from '@/components/neo4j/shared/ConnectionTargetBanner'
import { useGraphConnection } from '@/components/neo4j/shared/useGraphConnection'
import { CreateEntityDialog } from '@/features/manual-graph/CreateEntityDialog'
import type { CanvasNode } from '@/features/manual-graph/graph-node'
import { useCanvasEntityCreation } from '@/features/manual-graph/hooks/useCanvasEntityCreation'
import { useCreateManualRelationship } from '@/features/manual-graph/hooks/useManualGraph'
import { graphApi } from '@/services/api/graph'
import { fetchPatternUnionLegacy as fetchPatternUnion } from '@/services/api/pattern-query-union'
import type { ActiveGraphFilter } from '@/types/graph-query'

const EMPTY_FILTERS: ActiveGraphFilter[] = []

interface KnowledgeGraphViewProps {
  caseId: string
  defaultLayout?: string
  /** External layout control - when this changes, the graph will apply the new layout */
  externalLayout?: string
  onGraphDataLoaded?: (nodes: GraphNode[], edges: GraphEdge[]) => void
  onNodeSelected?: (node: SelectedNode | null) => void
  timeFilter?: { start: string; end: string } | null
  playheadTime?: string | null
  externalSelectedNodeId?: string | null
  onExternalNodeHandled?: () => void
  externalClearSelectionSignal?: number
  componentFilter?: { componentId: number; label: string } | null
  activeGraphFilters?: ActiveGraphFilter[]
  onAddFilter?: (filter: ActiveGraphFilter) => void
  onRegisterGraphActionHandler?: (handler: (action: any) => void) => void
}

interface GraphMetrics {
  totalNodes: number
  displayedNodes: number
}

function getStoredLayout(caseId: string): string {
  try {
    const v = localStorage.getItem(`case:${caseId}:knowledgeGraphLayout`)
    return v ? JSON.parse(v) : 'fcose'
  } catch {
    return 'fcose'
  }
}

function setStoredLayout(caseId: string, layout: string) {
  try {
    localStorage.setItem(`case:${caseId}:knowledgeGraphLayout`, JSON.stringify(layout))
  } catch {}
}

function mapNode(n: any): GraphNode {
  return {
    id: n.id,
    name: n.name,
    labels: n.labels,
    typeLabel:
      n.typeLabel ||
      n.labels?.find((l: string) => !l.startsWith('__') && l !== 'Node') ||
      'Unknown',
    properties: n.properties || {},
  }
}

function mapEdge(r: any): GraphEdge {
  return {
    id: r.id,
    source: r.source,
    target: r.target,
    type: r.type,
    properties: r.properties || {},
  }
}

/**
 * Add nodes and edges to every loaded knowledge-graph slice of the case,
 * skipping ids it already holds — expanded neighbours, a hand-made entity, a
 * focused node from outside the slice all join the canvas this way.
 */
function mergeIntoGraphCache(
  queryClient: ReturnType<typeof useQueryClient>,
  caseId: string,
  nodes: CanvasNode[],
  edges: ReadonlyArray<{ id: string }>
) {
  queryClient.setQueriesData<any>(
    { queryKey: ['case', caseId, 'knowledge-graph'] },
    (prev: any) => {
      if (!prev) return prev
      const knownNodes = new Set((prev.nodes || []).map((n: any) => n.id))
      const knownEdges = new Set((prev.relationships || []).map((r: any) => r.id))
      const freshNodes = nodes.filter(n => !knownNodes.has(n.id))
      const mergedNodes = [...(prev.nodes || []), ...freshNodes]
      return {
        ...prev,
        nodes: mergedNodes,
        relationships: [...(prev.relationships || []), ...edges.filter(e => !knownEdges.has(e.id))],
        total: typeof prev.total === 'number' ? prev.total + freshNodes.length : mergedNodes.length,
      }
    }
  )
}

export function KnowledgeGraphView({
  caseId,
  defaultLayout = 'fcose',
  externalLayout,
  onGraphDataLoaded,
  onNodeSelected,
  timeFilter,
  playheadTime,
  externalSelectedNodeId,
  onExternalNodeHandled,
  externalClearSelectionSignal,
  componentFilter,
  activeGraphFilters = EMPTY_FILTERS,
  onAddFilter,
  onRegisterGraphActionHandler,
}: KnowledgeGraphViewProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null)
  const [selectedEdge, setSelectedEdge] = useState<SelectedEdge | null>(null)
  // Lifted from L#~638 so `handleNodeSelect` / `handleEdgeSelect` below can
  // force-open the detail sidebar on click (a re-click of the SAME node
  // wouldn't trigger the selection-driven effect, so we need an explicit
  // setter call). Keep next to `selectedNode` for hook order clarity.
  const [detailOpen, setDetailOpen] = useState(false)
  // Surfaced when the user tries to create a connection whose source or
  // target no longer exists in the case's Neo4j graph (entity merged,
  // deleted, or otherwise removed since this view was loaded).
  const [divergenceWarning, setDivergenceWarning] = useState<{
    missingId: string
    missingName: string
    side: 'source' | 'target'
  } | null>(null)
  const graphRef = useRef<CytoscapeGraphRef>(null)
  const graphContainerRef = useRef<HTMLDivElement | null>(null)
  const onGraphDataLoadedRef = useRef(onGraphDataLoaded)
  onGraphDataLoadedRef.current = onGraphDataLoaded

  // Register graph action handler for AI Agent path visualization
  useEffect(() => {
    if (!onRegisterGraphActionHandler) return
    onRegisterGraphActionHandler((action: any) => {
      const cy = graphRef.current
      if (!cy) return
      switch (action.action_type) {
        case 'show_path':
          if (action.path_data) {
            cy.visualizePath({
              found: true,
              path_length: action.path_data.path_length,
              nodes: [],
              relationships: [],
              node_ids: action.path_data.node_ids,
              relationship_ids: action.path_data.relationship_ids,
              nodeIds: action.path_data.node_ids,
              edgeIds: action.path_data.relationship_ids,
              cost: null,
            })
          }
          break
        case 'highlight_nodes':
          if (action.entity_ids?.length) {
            cy.highlightNodes(action.entity_ids)
          }
          break
        case 'reset':
          cy.clearPath()
          break
      }
    })
  }, [onRegisterGraphActionHandler])

  // Toolbar state
  const [layout, setLayoutState] = useState(() =>
    defaultLayout !== 'fcose' ? defaultLayout : getStoredLayout(caseId)
  )
  const [isExpanded, setIsExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const exclusionState = useGraphExclusion(caseId)

  // Multi-select / bulk action state
  const [multiSelectedNodeIds, setMultiSelectedNodeIds] = useState<string[]>([])
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false)
  const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false)
  const [isDeletingNodes, setIsDeletingNodes] = useState(false)

  const clearMultiSelection = useCallback(() => {
    setMultiSelectedNodeIds([])
    graphRef.current?.clearSelection()
  }, [])

  const handleBulkDelete = useCallback(async () => {
    if (multiSelectedNodeIds.length === 0) return
    setIsDeletingNodes(true)
    try {
      await graphApi.batchDeleteNodes(caseId, multiSelectedNodeIds)
      toast.success(`Deleted ${multiSelectedNodeIds.length} nodes from the graph`)
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'knowledge-graph'] })
      setIsBulkDeleteDialogOpen(false)
      clearMultiSelection()
    } catch (error) {
      console.error('Error deleting nodes:', error)
      toast.error('Failed to delete nodes')
    } finally {
      setIsDeletingNodes(false)
    }
  }, [caseId, multiSelectedNodeIds, queryClient, clearMultiSelection])

  const handleGroupCreated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['case', caseId, 'knowledge-graph'] })
    clearMultiSelection()
  }, [caseId, queryClient, clearMultiSelection])

  // Stable serializable key parts
  const filterIds = useMemo(() => activeGraphFilters.map(f => f.id).sort(), [activeGraphFilters])

  // Fetch graph data via React Query (chip-driven only — custom views live in
  // the investigations store and are rendered by the Graph Views route).
  const {
    data: graphData,
    isLoading: loading,
    isFetching: isRefreshing,
    refetch,
  } = useQuery({
    queryKey: ['case', caseId, 'knowledge-graph', componentFilter?.componentId ?? null, filterIds],
    queryFn: async () => {
      if (activeGraphFilters.length > 0) {
        return fetchPatternUnion(caseId, activeGraphFilters)
      }

      return graphApi.getKnowledgeGraph(caseId, {
        limit: 500,
        componentId: componentFilter?.componentId,
      })
    },
  })

  // Derive nodes, edges, metrics from effective data
  const loadedNodes = useMemo<GraphNode[]>(() => (graphData?.nodes || []).map(mapNode), [graphData])
  const loadedEdges = useMemo<GraphEdge[]>(
    () => (graphData?.relationships || []).map(mapEdge),
    [graphData]
  )

  const { nodes, edges } = useMemo(
    () => filterFaceEvidence(loadedNodes, loadedEdges),
    [loadedNodes, loadedEdges]
  )
  const graphMetrics = useMemo<GraphMetrics | null>(
    () =>
      graphData
        ? { totalNodes: graphData.total, displayedNodes: graphData.nodes?.length || 0 }
        : null,
    [graphData]
  )

  // Notify parent when graph data changes — fire for empty results too, so the
  // parent can clear stale state when a filter produces zero matches.
  useEffect(() => {
    if (!graphData) return
    onGraphDataLoadedRef.current?.(nodes, edges)
  }, [graphData, nodes, edges])

  const nodesRef = useRef(nodes)
  nodesRef.current = nodes

  const entityTypesWithCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const n of nodes) {
      const t = n.typeLabel || 'Unknown'
      counts.set(t, (counts.get(t) || 0) + 1)
    }
    return Array.from(counts.entries()).map(([label, count]) => ({ label, count }))
  }, [nodes])

  // Create-connection flow — ring menu "Create Connection" → user taps
  // target node (intercepted in `handleNodeSelect`) → relationship-type
  // dialog (the schema's patterns between the two types) → the v1 write,
  // which checks the schema and stamps the author → refetch refreshes the canvas.
  const createManualRelationship = useCreateManualRelationship(caseId)
  const graphConnection = useGraphConnection({
    onCreateConnection: async (sourceId, targetId, _sName, _tName, relationshipType) => {
      await createManualRelationship.mutateAsync({
        source_id: sourceId,
        target_id: targetId,
        type: relationshipType,
      })
      await refetch()
    },
  })

  // Create-entity flow — toolbar button or "Create entity here" on the empty
  // canvas → schema-driven dialog → the new node joins the loaded slice at the
  // right-click point, selected.
  const addNodesToView = useCallback(
    (added: CanvasNode[]) => mergeIntoGraphCache(queryClient, caseId, added, []),
    [caseId, queryClient]
  )
  const entityCreation = useCanvasEntityCreation({
    caseId,
    graphRef,
    nodes,
    addNodes: addNodesToView,
  })

  // Handlers
  const handleNodeSelect = useCallback(
    (node: SelectedNode | null) => {
      // Mirror Cytoscape's internal selection into the view's state so the
      // detail sidebar can open. We also force the panel open here (in
      // addition to the selection-driven effect below) so re-clicking the
      // SAME node after dismissing the sidebar still re-opens it — when
      // selection is unchanged the effect at L#~648 doesn't re-fire.
      if (node) {
        setSelectedNode(node)
        setSelectedEdge(null)
        setDetailOpen(true)
      } else {
        setSelectedNode(null)
        setSelectedEdge(null)
      }
      onNodeSelected?.(node)
    },
    [onNodeSelected]
  )

  const handleEdgeSelect = useCallback((edge: SelectedEdge | null) => {
    setSelectedEdge(edge)
    setSelectedNode(null)
    // Same rationale as handleNodeSelect: force-open the detail panel on
    // every edge click — even a re-click of the currently-selected edge.
    if (edge) setDetailOpen(true)
  }, [])

  const handleRefresh = useCallback(() => {
    refetch()
  }, [refetch])

  const handleLayoutChange = useCallback(
    (newLayout: string) => {
      setLayoutState(newLayout)
      setStoredLayout(caseId, newLayout)
    },
    [caseId]
  )

  // External layout control: sync external layout changes to internal state
  useEffect(() => {
    if (externalLayout && externalLayout !== layout) {
      setLayoutState(externalLayout)
      setStoredLayout(caseId, externalLayout)
    }
  }, [externalLayout, caseId, layout])

  // Track whether the initial Cytoscape layout has had time to settle.
  // The first external selection (right after the graph loads) needs a
  // small warm-up so `selectNodeAndCenter` finds the node positioned;
  // every selection after that should be instant.
  const layoutReadyRef = useRef(false)
  useEffect(() => {
    if (!graphData) return
    const t = setTimeout(() => {
      layoutReadyRef.current = true
    }, 600)
    return () => clearTimeout(t)
  }, [graphData])

  // Cross-view selection: external node ID → focus + center in the canvas
  // only. The detail popup is reserved for explicit ring-menu activation —
  // external selection (e.g. from the map or timeline) must NOT auto-open it.
  useEffect(() => {
    if (!externalSelectedNodeId) return
    const node = nodesRef.current.find(n => n.id === externalSelectedNodeId)
    if (!node) {
      // Outside the loaded slice (e.g. an entity just added from Research):
      // load it into the view, then the placement hook centres it.
      if (!graphData) return
      void entityCreation.reveal(externalSelectedNodeId)
      onExternalNodeHandled?.()
      return
    }

    const delay = layoutReadyRef.current ? 0 : 600
    if (delay === 0) {
      graphRef.current?.selectNodeAndCenter(externalSelectedNodeId)
      onExternalNodeHandled?.()
      return
    }
    const timer = setTimeout(() => {
      graphRef.current?.selectNodeAndCenter(externalSelectedNodeId)
      onExternalNodeHandled?.()
    }, delay)
    return () => clearTimeout(timer)
  }, [externalSelectedNodeId, onExternalNodeHandled, nodes, graphData, entityCreation.reveal])

  // Cross-view deselection: a monotonic bump from the parent clears any
  // current selection in the graph (nodes, edges, focus dimming, sidebar).
  const clearSignalFirstRef = useRef(true)
  useEffect(() => {
    if (externalClearSelectionSignal === undefined) return
    if (clearSignalFirstRef.current) {
      clearSignalFirstRef.current = false
      return
    }
    graphRef.current?.clearSelection()
    setSelectedNode(null)
    setSelectedEdge(null)
  }, [externalClearSelectionSignal])

  // Search — client-side filter over loaded nodes
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return []
    const q = searchQuery.toLowerCase()
    return nodes
      .filter(n => n.name?.toLowerCase().includes(q))
      .slice(0, 10)
      .map(n => ({ id: n.id, name: n.name || n.id, typeLabel: n.typeLabel }))
  }, [nodes, searchQuery])

  const handleSearchSelect = useCallback(
    (node: { id: string; name: string; typeLabel?: string }) => {
      graphRef.current?.selectNodeAndCenter(node.id)
      setSearchQuery('')
      setSearchOpen(false)
    },
    []
  )

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setSearchOpen(false)
  }, [])

  // Hard-filter graph data by the time-range window only (playhead no longer
  // removes anything — it drives the dim state computed further below).
  const { filteredNodes, filteredEdges } = useMemo(() => {
    const { hiddenEntityTypes, hiddenEntityIds } = exclusionState.exclusion
    const hiddenTypes = new Set(hiddenEntityTypes)
    const hiddenIds = new Set(hiddenEntityIds)

    let visibleNodes = nodes.filter(
      n => !hiddenTypes.has(n.typeLabel || 'Unknown') && !hiddenIds.has(n.id)
    )

    if (!timeFilter) {
      const visibleNodeIds = new Set(visibleNodes.map(n => n.id))
      const visibleEdges = edges.filter(
        e => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target)
      )
      return { filteredNodes: visibleNodes, filteredEdges: visibleEdges }
    }

    const start = new Date(timeFilter.start).getTime()
    const rangeEnd = new Date(timeFilter.end).getTime()

    const visibleNodeIds = new Set(visibleNodes.map(n => n.id))
    const visibleEdges = edges.filter(e => {
      if (!visibleNodeIds.has(e.source) || !visibleNodeIds.has(e.target)) return false
      const p = e.properties || {}
      const ts = p.timestamp || p.occurred_at || p.posted_at
      if (!ts) return true // structural edge — always visible
      const t = new Date(String(ts)).getTime()
      return t >= start && t <= rangeEnd
    })

    const connectedNodeIds = new Set<string>()
    for (const e of visibleEdges) {
      connectedNodeIds.add(e.source)
      connectedNodeIds.add(e.target)
    }
    const allEdgeNodeIds = new Set<string>()
    for (const e of edges) {
      allEdgeNodeIds.add(e.source)
      allEdgeNodeIds.add(e.target)
    }
    visibleNodes = visibleNodes.filter(n => connectedNodeIds.has(n.id) || !allEdgeNodeIds.has(n.id))

    return { filteredNodes: visibleNodes, filteredEdges: visibleEdges }
  }, [nodes, edges, timeFilter, exclusionState.exclusion])

  // Temporal ghost state: which nodes/edges should render dimmed because they
  // are "in the future" relative to the playhead. Structural (timestampless)
  // edges are always treated as present; nodes that only appear through
  // still-future edges are dimmed too, so the graph feels consistent.
  const { dimmedNodeIds, dimmedEdgeIds } = useMemo(() => {
    const dimmedEdges = new Set<string>()
    const dimmedNodes = new Set<string>()
    if (!playheadTime) return { dimmedNodeIds: dimmedNodes, dimmedEdgeIds: dimmedEdges }

    const cursorMs = new Date(playheadTime).getTime()

    for (const e of filteredEdges) {
      const p = e.properties || {}
      const ts = p.timestamp || p.occurred_at || p.posted_at
      if (!ts) continue
      const t = new Date(String(ts)).getTime()
      if (t > cursorMs) dimmedEdges.add(e.id)
    }

    // A node is "live" (not dimmed) when it has at least one non-dimmed edge
    // attached — either structural (no timestamp) or past/present temporal.
    const liveNodeIds = new Set<string>()
    for (const e of filteredEdges) {
      if (!dimmedEdges.has(e.id)) {
        liveNodeIds.add(e.source)
        liveNodeIds.add(e.target)
      }
    }

    for (const n of filteredNodes) {
      // Isolated nodes (no edges at all) are always static → never dimmed.
      // Nodes with edges but no live edge → dimmed.
      const hasAnyEdge = filteredEdges.some(e => e.source === n.id || e.target === n.id) || false
      if (hasAnyEdge && !liveNodeIds.has(n.id)) dimmedNodes.add(n.id)
    }

    return { dimmedNodeIds: dimmedNodes, dimmedEdgeIds: dimmedEdges }
  }, [filteredNodes, filteredEdges, playheadTime])

  // For the Clusters layout, synthesize compound parent nodes from the Louvain
  // component_id on each node. Cytoscape's fcose then uses these compounds as
  // bounding boxes so communities physically separate on screen. Nodes without
  // a component_id stay top-level as "outliers".
  const clusteredNodes = useMemo<GraphNode[]>(() => {
    if (layout !== 'clusters') return filteredNodes

    const byComp = new Map<number, GraphNode[]>()
    for (const n of filteredNodes) {
      const compRaw = (n.properties as any)?.component_id
      if (compRaw == null) continue
      const comp = Number(compRaw)
      if (!byComp.has(comp)) byComp.set(comp, [])
      byComp.get(comp)!.push(n)
    }

    // If there are 0 or 1 distinct components, degrade to normal fcose
    // (no compounds) — still useful, still correct.
    if (byComp.size < 2) return filteredNodes

    const sortedComps = [...byComp.keys()].sort((a, b) => a - b)
    const compounds: GraphNode[] = sortedComps.map((comp, idx) => ({
      id: `__cluster_${comp}`,
      name: `Cluster ${String.fromCharCode(65 + idx)}`,
      labels: ['CaseCompound'],
      typeLabel: 'CaseCompound',
      properties: { component_id: comp, size: byComp.get(comp)!.length },
      isCompound: true,
    }))

    const reparented = filteredNodes.map(n => {
      const compRaw = (n.properties as any)?.component_id
      if (compRaw == null) return n
      return { ...n, parent: `__cluster_${Number(compRaw)}` }
    })

    return [...compounds, ...reparented]
  }, [layout, filteredNodes])

  // ---- Bloom-style popup plumbing -----------------------------------------

  const HOVER_DEBOUNCE_MS = 300
  // Offset (px) between the entity center and the popup's top-left corner, so
  // the cursor sits just outside the popup when hovering.
  const HOVER_POPUP_OFFSET = 14
  type HoverTargetKind = 'node' | 'edge'
  type HoverTarget = {
    kind: HoverTargetKind
    id: string
    selection: SelectedNode | SelectedEdge
    /** Rendered position of the entity relative to the graph container. */
    position: { x: number; y: number }
  }

  const [hoverTarget, setHoverTarget] = useState<HoverTarget | null>(null)
  // `detailOpen` is declared near the top of the component so click handlers
  // can force-open the sidebar. We just need the ref mirror here for the
  // hover-suppression logic below.
  const detailOpenRef = useRef(detailOpen)
  detailOpenRef.current = detailOpen
  // Ring (cxt) menu open state — while this is true the hover popup is
  // suppressed so it can't visually replace / dismiss the ring menu when the
  // user right-clicks an entity without hovering it first.
  const ringMenuOpenRef = useRef(false)

  // Every time selection changes, drive detail popup open state.
  useEffect(() => {
    setDetailOpen(Boolean(selectedNode || selectedEdge))
  }, [selectedNode, selectedEdge])

  // Tracks the selected element's screen rect. Reads on cytoscape viewport
  // events keep the value fresh for any HTML overlays anchored to the node.
  const detailRectRef = useRef<DOMRect | null>(null)

  // Compute a renderedPosition equivalent that also works for edges (which
  // don't have a single point position — we use the midpoint of the rendered
  // bounding box, falling back to the average of source/target positions).
  const renderedCenter = useCallback(
    (
      el: cytoscape.SingularElementReturnValue | null | undefined
    ): { x: number; y: number } | null => {
      if (!el || el.length === 0) return null
      const anyEl = el as unknown as {
        isNode: () => boolean
        isEdge: () => boolean
        renderedPosition: () => { x: number; y: number } | undefined
        renderedBoundingBox: () => { x1: number; y1: number; x2: number; y2: number } | undefined
        source: () => { renderedPosition: () => { x: number; y: number } | undefined }
        target: () => { renderedPosition: () => { x: number; y: number } | undefined }
      }
      if (anyEl.isNode()) {
        const p = anyEl.renderedPosition()
        if (!p || typeof p.x !== 'number') return null
        return { x: p.x, y: p.y }
      }
      if (anyEl.isEdge()) {
        const bb = anyEl.renderedBoundingBox()
        if (bb && Number.isFinite(bb.x1) && Number.isFinite(bb.x2)) {
          return { x: (bb.x1 + bb.x2) / 2, y: (bb.y1 + bb.y2) / 2 }
        }
        const s = anyEl.source().renderedPosition()
        const t = anyEl.target().renderedPosition()
        if (s && t) return { x: (s.x + t.x) / 2, y: (s.y + t.y) / 2 }
      }
      return null
    },
    []
  )

  // Signal subscribers that re-tick the anchors on pan/zoom.
  const viewportSubscribersRef = useRef(new Set<() => void>())
  const notifyViewport = useCallback(() => {
    // Refresh the detail rect and live-update hover position so popups
    // follow their entities while the user pans/zooms.
    const cy = graphRef.current?.getCy?.() ?? null
    if (cy) {
      const container = cy.container() as HTMLElement | null
      const containerRect = container?.getBoundingClientRect()
      if (containerRect) {
        const selId = selectedNode?.id ?? selectedEdge?.id ?? null
        if (selId) {
          const el = cy.getElementById(selId)
          const center = renderedCenter(el)
          if (center) {
            detailRectRef.current = new DOMRect(
              containerRect.left + center.x,
              containerRect.top + center.y,
              1,
              1
            )
          }
        }
        if (hoverTarget) {
          const el = cy.getElementById(hoverTarget.id)
          const center = renderedCenter(el)
          if (
            center &&
            (center.x !== hoverTarget.position.x || center.y !== hoverTarget.position.y)
          ) {
            setHoverTarget({ ...hoverTarget, position: center })
          }
        }
      }
    }
    for (const cb of viewportSubscribersRef.current) cb()
  }, [selectedNode, selectedEdge, hoverTarget, renderedCenter])

  const subscribeViewport = useCallback((cb: () => void) => {
    viewportSubscribersRef.current.add(cb)
    return () => {
      viewportSubscribersRef.current.delete(cb)
    }
  }, [])

  // Wire Cytoscape mouseover/mouseout + viewport.
  // On first mount Cytoscape may not be initialized yet (`getCy()` returns
  // null). Poll via rAF until it resolves, then attach. Without this, the
  // hover popup silently refuses to work on initial page load and only
  // starts once some other state change re-runs the effect later.
  useEffect(() => {
    let cy: cytoscape.Core | null = null
    let rafHandle: number | null = null
    let attached = false
    let hoverTimer: ReturnType<typeof setTimeout> | null = null
    let pendingTarget: HoverTarget | null = null

    // Compound/cluster parent nodes generated by the clusters layout have
    // their own in-graph hover chrome — don't also fire the entity hover popup
    // for them. (Compound node ids are prefixed `__cluster_`.)
    const isCompound = (id: string | undefined | null) =>
      typeof id === 'string' && id.startsWith('__cluster_')

    const handleNodeOver = (evt: cytoscape.EventObject) => {
      if (detailOpenRef.current || ringMenuOpenRef.current) return
      const data = evt.target.data() as {
        id: string
        name?: string
        labels?: string[]
        typeLabel?: string
        properties?: Record<string, unknown>
      }
      if (isCompound(data.id)) return
      const position = renderedCenter(evt.target)
      if (!position) return
      const selection: SelectedNode = {
        id: data.id,
        name: data.name ?? data.id,
        labels: data.labels ?? [],
        typeLabel: data.typeLabel,
        properties: data.properties ?? {},
      }
      pendingTarget = { kind: 'node', id: data.id, selection, position }
      if (hoverTimer) clearTimeout(hoverTimer)
      hoverTimer = setTimeout(() => {
        setHoverTarget(pendingTarget)
      }, HOVER_DEBOUNCE_MS)
    }

    const handleEdgeOver = (evt: cytoscape.EventObject) => {
      if (detailOpenRef.current || ringMenuOpenRef.current) return
      const data = evt.target.data() as {
        id: string
        type: string
        source: string
        target: string
        properties?: Record<string, unknown>
      }
      const position = renderedCenter(evt.target)
      if (!position) return
      const selection: SelectedEdge = {
        id: data.id,
        type: data.type,
        source: data.source,
        target: data.target,
        sourceName: cy?.getElementById(data.source).data('name') as string | undefined,
        targetName: cy?.getElementById(data.target).data('name') as string | undefined,
        properties: data.properties,
      }
      pendingTarget = { kind: 'edge', id: data.id, selection, position }
      if (hoverTimer) clearTimeout(hoverTimer)
      hoverTimer = setTimeout(() => {
        setHoverTarget(pendingTarget)
      }, HOVER_DEBOUNCE_MS)
    }

    const handleOut = (evt: cytoscape.EventObject) => {
      // Only clear if the mouseout is for the element we're actually tracking.
      // Cytoscape frequently fires `mouseout` AFTER `mouseover` when the
      // pointer crosses directly between two elements — without this guard,
      // the stale mouseout would erase the fresh mouseover's pendingTarget.
      const outId = evt.target.data('id') as string | undefined
      if (pendingTarget && pendingTarget.id !== outId) return
      if (hoverTimer) clearTimeout(hoverTimer)
      hoverTimer = null
      pendingTarget = null
      setHoverTarget(null)
    }

    // Mirror the cxtmenu plugin's open/close events. `cxttapstart` / `taphold`
    // open the ring menu; `cxttapend` / `tapend` close it. While open we
    // suppress the hover popup so it can't visually displace the menu when
    // the user right-clicks an entity without hovering it first.
    const handleRingOpen = () => {
      ringMenuOpenRef.current = true
      if (hoverTimer) clearTimeout(hoverTimer)
      hoverTimer = null
      pendingTarget = null
      setHoverTarget(null)
    }
    const handleRingClose = () => {
      ringMenuOpenRef.current = false
    }

    const attach = (instance: cytoscape.Core) => {
      cy = instance
      attached = true
      instance.on('mouseover', 'node', handleNodeOver)
      instance.on('mouseover', 'edge', handleEdgeOver)
      instance.on('mouseout', 'node', handleOut)
      instance.on('mouseout', 'edge', handleOut)
      instance.on('cxttapstart taphold', handleRingOpen)
      instance.on('cxttapend tapend', handleRingClose)
      instance.on('viewport pan zoom', notifyViewport)
    }

    const tryAttach = () => {
      const instance = graphRef.current?.getCy?.() ?? null
      if (instance) {
        attach(instance)
        return
      }
      rafHandle = requestAnimationFrame(tryAttach)
    }

    tryAttach()

    return () => {
      if (hoverTimer) clearTimeout(hoverTimer)
      if (rafHandle !== null) cancelAnimationFrame(rafHandle)
      if (attached && cy) {
        cy.off('mouseover', 'node', handleNodeOver)
        cy.off('mouseover', 'edge', handleEdgeOver)
        cy.off('mouseout', 'node', handleOut)
        cy.off('mouseout', 'edge', handleOut)
        cy.off('cxttapstart taphold', handleRingOpen)
        cy.off('cxttapend tapend', handleRingClose)
        cy.off('viewport pan zoom', notifyViewport)
      }
    }
  }, [notifyViewport, renderedCenter])

  // Suppress hover whenever the detail popup opens.
  useEffect(() => {
    if (detailOpen) setHoverTarget(null)
  }, [detailOpen])

  // Populate detail rect whenever the selection changes.
  useEffect(() => {
    const cy = graphRef.current?.getCy?.()
    if (!cy) {
      detailRectRef.current = null
      return
    }
    const container = cy.container() as HTMLElement | null
    const cr = container?.getBoundingClientRect()
    if (!cr) {
      detailRectRef.current = null
      return
    }
    const id = selectedNode?.id ?? selectedEdge?.id ?? null
    if (!id) {
      detailRectRef.current = null
      return
    }
    const el = cy.getElementById(id)
    const center = renderedCenter(el)
    if (!center) {
      detailRectRef.current = null
      return
    }
    detailRectRef.current = new DOMRect(cr.left + center.x, cr.top + center.y, 1, 1)
  }, [selectedNode, selectedEdge, renderedCenter])

  const relationshipSummaries = useMemo(() => {
    const id = selectedNode?.id
    if (!id) return []
    return summarizeEdges(id, edges, nodes)
  }, [selectedNode, edges, nodes])

  const handleAddToGraph = useCallback(
    (addedNodes: NeighborNode[], addedEdges: NeighborEdge[]) => {
      queryClient.setQueriesData<
        | {
            nodes: NeighborNode[]
            relationships: NeighborEdge[]
            total?: number
          }
        | undefined
      >({ queryKey: ['case', caseId, 'knowledge-graph'] }, prev => {
        if (!prev) return prev
        const merged = mergeNeighborsIntoGraph({
          hostNodes: prev.nodes ?? [],
          hostEdges: prev.relationships ?? [],
          neighborResponse: { nodes: addedNodes, relationships: addedEdges },
          selectedIds: new Set(addedNodes.map(n => n.id)),
          hostNodeId: selectedNode?.id ?? '',
        })
        return {
          ...prev,
          nodes: merged.nodes,
          relationships: merged.edges,
          total: typeof prev.total === 'number' ? prev.total + merged.addedNodes.length : undefined,
        }
      })
      if (addedNodes.length > 0 || addedEdges.length > 0) {
        toast.success(`Added ${addedNodes.length} neighbour${addedNodes.length === 1 ? '' : 's'}`)
      }
    },
    [caseId, queryClient, selectedNode]
  )

  const handleDeleteEntity = useCallback(
    async (nodeId: string) => {
      await graphApi.deleteNode(caseId, nodeId)
      // Optimistically remove the node and any incident edges from every
      // cached slice of the knowledge-graph query.
      queryClient.setQueriesData<
        | {
            nodes: { id: string }[]
            relationships: { source: string; target: string }[]
            total?: number
          }
        | undefined
      >({ queryKey: ['case', caseId, 'knowledge-graph'] }, prev => {
        if (!prev) return prev
        const nextNodes = (prev.nodes ?? []).filter(n => n.id !== nodeId)
        const nextRels = (prev.relationships ?? []).filter(
          r => r.source !== nodeId && r.target !== nodeId
        )
        return {
          ...prev,
          nodes: nextNodes,
          relationships: nextRels,
          total: typeof prev.total === 'number' ? Math.max(0, prev.total - 1) : undefined,
        }
      })
      // Drop cached node/neighbour details so stale data doesn't linger.
      queryClient.removeQueries({ queryKey: entityDetailsQueryKey(caseId, nodeId) })
      queryClient.removeQueries({ queryKey: entityNeighborsQueryKey(caseId, nodeId) })
      setSelectedNode(null)
      setSelectedEdge(null)
      toast.success('Entity deleted')
    },
    [caseId, queryClient]
  )

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const isTruncated = graphMetrics ? nodes.length < graphMetrics.totalNodes : false

  return (
    <div className="relative h-full flex flex-col bg-background overflow-hidden">
      {/* Truncation warning */}
      {!componentFilter && isTruncated && graphMetrics && (
        <BannerDismissible nodeCount={nodes.length} totalNodes={graphMetrics.totalNodes} />
      )}

      {/* Graph + optional detail sidebar */}
      <div className="flex-1 flex overflow-hidden">
        <div ref={graphContainerRef} className="flex-1 min-w-0 relative">
          <TemporalDimBanner
            dimCount={dimmedNodeIds.size + dimmedEdgeIds.size}
            playheadTime={playheadTime ?? null}
            filterEnd={timeFilter?.end ?? null}
            caseId={caseId}
          />
          <CytoscapeGraph
            ref={graphRef}
            caseId={caseId}
            textureOnViewport
            nodes={clusteredNodes}
            edges={filteredEdges}
            dimmedNodeIds={dimmedNodeIds}
            dimmedEdgeIds={dimmedEdgeIds}
            onNodeSelect={(node: SelectedNode | null) => {
              // While Create-Connection mode is active, a tap on a *different*
              // node is the target-pick gesture — route to the relationship
              // dialog instead of normal selection.
              if (
                node &&
                graphConnection.isConnectionMode &&
                graphConnection.connectionSourceId &&
                graphConnection.connectionSourceId !== node.id
              ) {
                graphConnection.handleConnectionSelect(
                  node.id,
                  node.name,
                  node.typeLabel,
                  node.labels,
                  id => filteredNodes.find(n => n.id === id)?.name || 'Unknown',
                  id => filteredNodes.find(n => n.id === id)?.typeLabel,
                  id => filteredNodes.find(n => n.id === id)?.labels
                )
                return
              }
              handleNodeSelect(node)
            }}
            onMultiSelect={setMultiSelectedNodeIds}
            onAddConnection={nodeId => graphConnection.startConnectionMode(nodeId)}
            onCreateEntity={entityCreation.openAt}
            onExpandNode={async (nodeId: string) => {
              try {
                const { nodes: newNodes, relationships: newEdges } =
                  await graphApi.getNodeNeighbors(caseId, nodeId)

                const existingNodeIds = new Set((graphData?.nodes || []).map((n: any) => n.id))
                const existingEdgeIds = new Set(
                  (graphData?.relationships || []).map((r: any) => r.id)
                )
                const addedNodes = newNodes.filter(n => !existingNodeIds.has(n.id))
                const addedEdges = newEdges.filter(r => !existingEdgeIds.has(r.id))

                if (addedNodes.length === 0 && addedEdges.length === 0) {
                  toast.info('No new neighbors to add')
                  return
                }

                mergeIntoGraphCache(queryClient, caseId, addedNodes, addedEdges)

                toast.success(
                  `Added ${addedNodes.length} neighbor${addedNodes.length === 1 ? '' : 's'}`
                )
              } catch (err) {
                console.error('Failed to expand node:', err)
                toast.error('Failed to expand node')
              }
            }}
            onEdgeSelect={handleEdgeSelect}
            layout={layout as any}
            onLayoutChange={handleLayoutChange}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            nodeSearchQuery={searchQuery}
            onNodeSearchChange={q => {
              setSearchQuery(q)
              setSearchOpen(true)
            }}
            nodeSearchResults={searchResults}
            isNodeSearchOpen={searchOpen && searchResults.length > 0}
            onSearchNodeSelect={handleSearchSelect}
            onClearSearch={handleClearSearch}
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
            onToggleFilter={() => setShowFilterPanel(prev => !prev)}
            showFilterPanel={showFilterPanel}
          />

          <MultiSelectActionBar
            selectedCount={multiSelectedNodeIds.length}
            onClear={clearMultiSelection}
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-blue-400 hover:text-blue-200 hover:bg-blue-500/20 max-md:h-9"
              onClick={() => setIsCreateGroupDialogOpen(true)}
            >
              <FolderOpen className="h-3 w-3 mr-1" />
              Create Group
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-red-400 hover:text-red-200 hover:bg-red-500/20 max-md:h-9"
              onClick={() => setIsBulkDeleteDialogOpen(true)}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Delete
            </Button>
          </MultiSelectActionBar>

          {/* Stats overlay */}
          <div className="absolute bottom-3 left-3 flex items-center gap-2 text-[10px] text-muted-foreground bg-background/80 rounded px-2 py-1 border border-border">
            {graphMetrics ? (
              <>
                <span>
                  Showing {filteredNodes.length.toLocaleString()} of{' '}
                  {graphMetrics.totalNodes.toLocaleString()} nodes
                </span>
                <span>·</span>
                <span>{filteredEdges.length.toLocaleString()} edges</span>
              </>
            ) : (
              <span>
                {filteredNodes.length.toLocaleString()} nodes ·{' '}
                {filteredEdges.length.toLocaleString()} edges
              </span>
            )}
          </div>

          {/* Hover preview anchored to the entity but offset to the bottom-right
              of the cursor so the natural upward mouse motion doesn't collide
              with it. Flips to the opposite side when near the container edge. */}
          {hoverTarget && !detailOpen ? (
            <div
              className="pointer-events-none absolute z-30"
              style={(() => {
                // w-72 = 288px; height is content-driven, ~150px is a typical
                // upper bound for badge + title + 3 properties.
                const POPUP_W = 288
                const POPUP_H_ESTIMATE = 150
                const cw = graphContainerRef.current?.clientWidth ?? 0
                const ch = graphContainerRef.current?.clientHeight ?? 0
                const flipX = cw > 0 && hoverTarget.position.x + HOVER_POPUP_OFFSET + POPUP_W > cw
                const flipY =
                  ch > 0 && hoverTarget.position.y + HOVER_POPUP_OFFSET + POPUP_H_ESTIMATE > ch
                return {
                  left: flipX
                    ? hoverTarget.position.x - HOVER_POPUP_OFFSET - POPUP_W
                    : hoverTarget.position.x + HOVER_POPUP_OFFSET,
                  top: flipY
                    ? hoverTarget.position.y - HOVER_POPUP_OFFSET - POPUP_H_ESTIMATE
                    : hoverTarget.position.y + HOVER_POPUP_OFFSET,
                }
              })()}
            >
              <EntityHoverBody selection={hoverTarget.selection} />
            </div>
          ) : null}

          {/* Floating "open detail sidebar" affordance — shows when the
              user has a node/edge selected but has dismissed the detail
              panel. Matches the Map view's SidebarOpenButton placement
              (top-right of the canvas) so the gesture stays consistent
              across views. Clicking re-opens the sidebar with the
              preserved selection — no re-clicking the entity needed. */}
          {!detailOpen && (selectedNode || selectedEdge) ? (
            <div className="pointer-events-auto absolute right-3 top-3 z-30 max-md:top-4 max-md:right-16">
              <SidebarOpenButton
                onClick={() => setDetailOpen(true)}
                label={t('graph.detail.openSidebar', 'Show details')}
              />
            </div>
          ) : null}
        </div>

        {/* Filter panel */}
        {showFilterPanel && (
          <div className="w-full border-border bg-background overflow-y-auto shrink-0 max-lg:absolute max-lg:inset-0 max-lg:z-40 max-lg:flex max-lg:flex-col lg:w-72 lg:border-l">
            {/* Below lg (phone, portrait tablet) the panel covers the canvas: beside a
                420 px detail pane it would leave the canvas no width. The type list
                scrolls in the space above a pinned query builder. */}
            <div className="max-lg:min-h-0 max-lg:flex-1 lg:h-full">
              <ExploreFilterPanel
                nodes={nodes}
                exclusionState={exclusionState}
                onClose={() => setShowFilterPanel(false)}
              />
            </div>
            <div className="px-3 pb-3 max-lg:max-h-[45%] max-lg:shrink-0 max-lg:overflow-y-auto max-lg:border-t max-lg:border-border max-lg:pt-3">
              <GraphQueryBuilder
                caseId={caseId}
                entityTypes={entityTypesWithCounts}
                onAddFilter={onAddFilter}
              />
            </div>
          </div>
        )}

        {detailOpen && (selectedNode || selectedEdge) ? (
          <KnowledgeGraphDetailSidebar
            caseId={caseId}
            open={detailOpen}
            onClose={() => {
              // Just collapse the panel — keep selection so the floating
              // SidebarOpenButton (rendered above) can re-open with the
              // same context. Re-clicking the entity also works and will
              // force-open via handleNodeSelect / handleEdgeSelect.
              setDetailOpen(false)
            }}
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            relationships={relationshipSummaries}
            onAddToGraph={handleAddToGraph}
            onDeleteEntity={handleDeleteEntity}
          />
        ) : null}
      </div>

      <CreateGroupDialog
        open={isCreateGroupDialogOpen}
        onOpenChange={setIsCreateGroupDialogOpen}
        caseId={caseId}
        nodeIds={multiSelectedNodeIds}
        onGroupCreated={handleGroupCreated}
      />

      <BulkDeleteDialog
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        nodeCount={multiSelectedNodeIds.length}
        onConfirm={handleBulkDelete}
        isDeleting={isDeletingNodes}
      />

      <CreateRelationshipDialog
        open={graphConnection.showRelationshipDialog}
        onOpenChange={graphConnection.setShowRelationshipDialog}
        caseId={caseId}
        sourceEntity={graphConnection.pendingConnection?.source || null}
        targetEntity={graphConnection.pendingConnection?.target || null}
        onCreateRelationship={async option => {
          // Divergence pre-check against Neo4j — the local cy view may be
          // stale: the entity could have been merged, deleted, or removed
          // since the graph was last fetched. Probe the server and refuse
          // to write a connection that would dangle.
          const src = graphConnection.pendingConnection?.source
          const tgt = graphConnection.pendingConnection?.target
          if (!src || !tgt) return

          const [srcResult, tgtResult] = await Promise.allSettled([
            graphApi.getNode(caseId, src.id),
            graphApi.getNode(caseId, tgt.id),
          ])
          const hasSource = srcResult.status === 'fulfilled'
          const hasTarget = tgtResult.status === 'fulfilled'
          if (!hasSource || !hasTarget) {
            const missing = !hasSource ? src : tgt
            setDivergenceWarning({
              missingId: missing.id,
              missingName: missing.name,
              side: !hasSource ? 'source' : 'target',
            })
            graphConnection.setShowRelationshipDialog(false)
            graphConnection.cancelConnectionMode()
            return
          }
          await graphConnection.createRelationship(option)
        }}
      />

      <CreateEntityDialog {...entityCreation.dialogProps} />

      <Dialog open={!!divergenceWarning} onOpenChange={open => !open && setDivergenceWarning(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Graph diverged from case data</DialogTitle>
            <DialogDescription>
              The {divergenceWarning?.side} entity{' '}
              <span className="font-semibold text-foreground">
                "{divergenceWarning?.missingName}"
              </span>{' '}
              no longer exists in this case's graph. It may have been merged into another entity,
              deleted, or otherwise removed since this view was loaded.
              <br />
              <br />
              The connection was <span className="font-semibold">not</span> created. Refresh the
              graph to sync with the current case data, then try again — possibly against the entity
              it was merged into.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDivergenceWarning(null)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConnectionTargetBanner
        visible={graphConnection.isConnectionMode && !graphConnection.showRelationshipDialog}
        sourceName={
          nodes.find(n => n.id === graphConnection.connectionSourceId)?.name ?? 'Source entity'
        }
        sourceTypeLabel={nodes.find(n => n.id === graphConnection.connectionSourceId)?.typeLabel}
        onCancel={graphConnection.cancelConnectionMode}
      />
    </div>
  )
}
